// Modal that asks "Title + Tier" — used by Workbench Commit-to-silo and Library Move-to-silo.
import { useEffect, useState } from "react";
import { X, Anchor, BookOpen, Books, Warning } from "@phosphor-icons/react";

const TIERS = [
  {
    id: 1,
    label: "Tier 1 · Anchor",
    icon: Anchor,
    desc: "Always-active soul. Defines how the AI thinks. Recommended 100,000–150,000 chars.",
    soft_max: 150000,
  },
  {
    id: 2,
    label: "Tier 2 · Logic Core",
    icon: BookOpen,
    desc: "Heavy-weighted primary library. Searched first. Recommended 500,000–1,500,000 chars.",
    soft_max: 1500000,
  },
  {
    id: 3,
    label: "Tier 3 · Library",
    icon: Books,
    desc: "Raw archive — accessed only if Tiers 1 & 2 don't have the answer. Recommended 10M+ chars.",
    soft_max: Infinity,
  },
];

export default function TierPickerModal({
  open, onClose, onSubmit,
  title = "Commit to silo",
  subtitle,
  defaultTitle = "",
  contentLength = 0,
  showTitleField = true,
  showSiloField = false,
  showKeepOriginal = false,  // Feb 17, 2026 — Library move modal exposes a
                              // "Keep a copy at the source" toggle so Move ↔ Copy
                              // lives behind a single flag.
  silos = [],
  defaultSiloId = "",
  initialTier = 2,
  submitLabel = "Commit",
}) {
  const [name, setName] = useState(defaultTitle);
  const [tier, setTier] = useState(initialTier);
  const [siloId, setSiloId] = useState(defaultSiloId);
  const [keepOriginal, setKeepOriginal] = useState(false);

  useEffect(() => {
    if (open) {
      setName(defaultTitle);
      setTier(initialTier);
      setSiloId(defaultSiloId);
      setKeepOriginal(false);
    }
  }, [open, defaultTitle, initialTier, defaultSiloId]);

  if (!open) return null;

  const tooBigForAnchor = tier === 1 && contentLength > 150_000;
  const tooBigForLogic = tier === 2 && contentLength > 1_500_000;

  const submit = (e) => {
    e?.preventDefault();
    if (showTitleField && !name.trim()) return;
    if (showSiloField && !siloId) return;
    onSubmit?.({ title: name.trim(), tier, silo_id: siloId, keep_original: keepOriginal });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="tier-picker-modal">
      <div className="absolute inset-0 bg-[#1C1B1A]/40 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="modal-safe relative z-10 w-full max-w-lg bg-white border hairline rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b hairline">
          <div>
            <div className="font-serif-display text-lg leading-none text-[#1C1B1A]">{title}</div>
            {subtitle && <div className="text-xs text-[#6B6A68] mt-1">{subtitle}</div>}
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {showSiloField && (
            <div>
              <label className="block text-sm text-[#6B6A68] mb-1.5">Destination silo</label>
              <select data-testid="tier-picker-silo" value={siloId} onChange={(e) => setSiloId(e.target.value)}
                className="w-full bg-white border hairline rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]">
                <option value="">— Choose silo —</option>
                {silos.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}

          {showKeepOriginal && (
            <label className="flex items-start gap-3 p-3 rounded-md border hairline cursor-pointer hover:border-[#4A5D4E]/50 bg-[#FDFCF8]"
              data-testid="tier-picker-keep-original">
              <input
                type="checkbox"
                className="mt-0.5 accent-[#4A5D4E]"
                checked={keepOriginal}
                onChange={(e) => setKeepOriginal(e.target.checked)}
              />
              <div className="text-xs leading-relaxed">
                <div className="text-[#1C1B1A] font-medium text-sm">Keep a copy at the source</div>
                <div className="text-[#6B6A68] mt-0.5">
                  Leaves the original document in place and adds a copy at the destination. Off = a true move (original is removed).
                </div>
              </div>
            </label>
          )}

          {showTitleField && (
            <div>
              <label className="block text-sm text-[#6B6A68] mb-1.5">Title</label>
              <input data-testid="tier-picker-title" autoFocus value={name} onChange={(e) => setName(e.target.value)}
                className="w-full bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                placeholder="e.g., Foundational essay on grace" />
            </div>
          )}

          <div>
            <div className="block text-sm text-[#6B6A68] mb-2">Choose tier</div>
            <div className="space-y-2">
              {TIERS.map((t) => {
                const Icon = t.icon;
                const active = tier === t.id;
                return (
                  <label key={t.id} className={`flex gap-3 p-3 rounded-md border cursor-pointer transition-colors ${active ? "border-[#4A5D4E] bg-[#F0F1EE]" : "hairline hover:border-[#4A5D4E]/50"}`} data-testid={`tier-option-${t.id}`}>
                    <input type="radio" name="tier" value={t.id} checked={active} onChange={() => setTier(t.id)} className="mt-1.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[#1C1B1A] text-sm font-medium">
                        <Icon size={16} weight="duotone" /> {t.label}
                      </div>
                      <div className="text-xs text-[#6B6A68] mt-1 leading-relaxed">{t.desc}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {(tooBigForAnchor || tooBigForLogic) && (
            <div className="flex gap-2 items-start text-xs text-[#C16E5A] bg-[#FDF1ED] border border-[#F2D0C5] rounded-md px-3 py-2.5" data-testid="tier-warning">
              <Warning size={14} weight="duotone" className="mt-0.5 shrink-0" />
              <div>
                <strong>File too large for {tooBigForAnchor ? "Tier 1 (Anchor)" : "Tier 2 (Logic Core)"}.</strong>{" "}
                Moving here increases "Soul" accuracy but uses more credits per query. Recommend Tier {tooBigForAnchor ? "2 or 3" : "3"} for raw bulk.
              </div>
            </div>
          )}

          <div className="text-xs text-[#6B6A68]">
            Content size: <span className="font-mono">{contentLength.toLocaleString()}</span> chars
          </div>
        </div>

        <div className="px-6 py-3 bg-[#FDFCF8] border-t hairline flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE] text-sm">Cancel</button>
          <button type="submit" data-testid="tier-picker-submit"
            className="px-5 py-2 rounded-md bg-[#1C1B1A] text-white hover:bg-[#3A4A3E] text-sm disabled:opacity-50"
            disabled={(showTitleField && !name.trim()) || (showSiloField && !siloId)}>
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
